'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

export interface CubesProps {
  gridSize?: number;
  cubeSize?: number;
  maxAngle?: number;
  radius?: number;
  easing?: string;
  duration?: { enter: number; leave: number };
  cellGap?: number | { col?: number; row?: number };
  borderStyle?: string;
  faceColor?: string;
  shadow?: boolean | string;
  autoAnimate?: boolean;
  rippleOnClick?: boolean;
  rippleColor?: string;
  rippleSpeed?: number;
  /** Track the pointer across the whole window: every cube tilts to "face" the
   *  cursor, even when it's nowhere near the grid (vs. the default hover-only tilt). */
  followPointer?: boolean;
  /** Distance (px) from the field's centre at which the follow tilt maxes out. */
  followRange?: number;
  /** Nudges overall pointer-follow responsiveness; each cube still gets its own
   *  lagged speed so they trail the cursor individually. */
  followStagger?: number;
  /** Each cube independently fades out and back in at its own random cadence. */
  twinkle?: boolean;
  /** Overrides the default `w-1/2 max-md:w-11/12 aspect-square` root sizing. */
  className?: string;
}

const Cubes = ({
  gridSize = 10,
  cubeSize,
  maxAngle = 45,
  radius = 3,
  easing = 'power3.out',
  duration = { enter: 0.3, leave: 0.6 },
  cellGap,
  borderStyle = '1px solid #fff',
  faceColor = '#120F17',
  shadow = false,
  autoAnimate = true,
  rippleOnClick = true,
  rippleColor = '#fff',
  rippleSpeed = 2,
  followPointer = false,
  followRange = 500,
  followStagger = 0,
  twinkle = false,
  className,
}: CubesProps) => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userActiveRef = useRef(false);
  const simPosRef = useRef({ x: 0, y: 0 });
  const simTargetRef = useRef({ x: 0, y: 0 });
  const simRAFRef = useRef<number | null>(null);
  // Pointer-follow target (degrees) the per-cube lerp loop eases toward.
  const followTargetRef = useRef({ rotX: 0, rotY: 0 });
  const followRAFRef = useRef<number | null>(null);

  // Respect the user's reduced-motion preference: when set, we skip the idle
  // auto-animation (matches the site's existing globals.css handling).
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const colGap =
    typeof cellGap === 'number' ? `${cellGap}px` : cellGap?.col !== undefined ? `${cellGap.col}px` : '5%';
  const rowGap =
    typeof cellGap === 'number' ? `${cellGap}px` : cellGap?.row !== undefined ? `${cellGap.row}px` : '5%';

  const enterDur = duration.enter;
  const leaveDur = duration.leave;

  const tiltAt = useCallback(
    (rowCenter: number, colCenter: number) => {
      if (!sceneRef.current) return;
      sceneRef.current.querySelectorAll<HTMLElement>('.cube').forEach(cube => {
        const r = +cube.dataset.row!;
        const c = +cube.dataset.col!;
        const dist = Math.hypot(r - rowCenter, c - colCenter);
        if (dist <= radius) {
          const pct = 1 - dist / radius;
          const angle = pct * maxAngle;
          gsap.to(cube, {
            duration: enterDur,
            ease: easing,
            overwrite: true,
            rotateX: -angle,
            rotateY: angle,
          });
        } else {
          gsap.to(cube, {
            duration: leaveDur,
            ease: 'power3.out',
            overwrite: true,
            rotateX: 0,
            rotateY: 0,
          });
        }
      });
    },
    [radius, maxAngle, enterDur, leaveDur, easing]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      userActiveRef.current = true;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      const rect = sceneRef.current!.getBoundingClientRect();
      const cellW = rect.width / gridSize;
      const cellH = rect.height / gridSize;
      const colCenter = (e.clientX - rect.left) / cellW;
      const rowCenter = (e.clientY - rect.top) / cellH;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => tiltAt(rowCenter, colCenter));

      idleTimerRef.current = setTimeout(() => {
        userActiveRef.current = false;
      }, 3000);
    },
    [gridSize, tiltAt]
  );

  const resetAll = useCallback(() => {
    if (!sceneRef.current) return;
    sceneRef.current.querySelectorAll<HTMLElement>('.cube').forEach(cube =>
      gsap.to(cube, {
        duration: leaveDur,
        rotateX: 0,
        rotateY: 0,
        ease: 'power3.out',
      })
    );
  }, [leaveDur]);

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      userActiveRef.current = true;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      const rect = sceneRef.current!.getBoundingClientRect();
      const cellW = rect.width / gridSize;
      const cellH = rect.height / gridSize;

      const touch = e.touches[0];
      const colCenter = (touch.clientX - rect.left) / cellW;
      const rowCenter = (touch.clientY - rect.top) / cellH;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => tiltAt(rowCenter, colCenter));

      idleTimerRef.current = setTimeout(() => {
        userActiveRef.current = false;
      }, 3000);
    },
    [gridSize, tiltAt]
  );

  const onTouchStart = useCallback(() => {
    userActiveRef.current = true;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!sceneRef.current) return;
    resetAll();
  }, [resetAll]);

  const onClick = useCallback(
    (e: PointerEvent) => {
      if (!rippleOnClick || !sceneRef.current) return;
      const rect = sceneRef.current.getBoundingClientRect();
      const cellW = rect.width / gridSize;
      const cellH = rect.height / gridSize;

      const clientX = e.clientX;
      const clientY = e.clientY;

      const colHit = Math.floor((clientX - rect.left) / cellW);
      const rowHit = Math.floor((clientY - rect.top) / cellH);

      const baseRingDelay = 0.15;
      const baseAnimDur = 0.3;
      const baseHold = 0.6;

      const spreadDelay = baseRingDelay / rippleSpeed;
      const animDuration = baseAnimDur / rippleSpeed;
      const holdTime = baseHold / rippleSpeed;

      const rings: Record<number, HTMLElement[]> = {};
      sceneRef.current.querySelectorAll<HTMLElement>('.cube').forEach(cube => {
        const r = +cube.dataset.row!;
        const c = +cube.dataset.col!;
        const dist = Math.hypot(r - rowHit, c - colHit);
        const ring = Math.round(dist);
        if (!rings[ring]) rings[ring] = [];
        rings[ring].push(cube);
      });

      Object.keys(rings)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach(ring => {
          const delay = ring * spreadDelay;
          const faces = rings[ring].flatMap(cube =>
            Array.from(cube.querySelectorAll<HTMLElement>('.cube-face'))
          );

          gsap.to(faces, {
            backgroundColor: rippleColor,
            duration: animDuration,
            delay,
            ease: 'power3.out',
          });
          gsap.to(faces, {
            backgroundColor: faceColor,
            duration: animDuration,
            delay: delay + animDuration + holdTime,
            ease: 'power3.out',
          });
        });
    },
    [rippleOnClick, gridSize, faceColor, rippleColor, rippleSpeed]
  );

  useEffect(() => {
    if (!autoAnimate || reduced || !sceneRef.current) return;
    simPosRef.current = {
      x: Math.random() * gridSize,
      y: Math.random() * gridSize,
    };
    simTargetRef.current = {
      x: Math.random() * gridSize,
      y: Math.random() * gridSize,
    };
    const speed = 0.02;
    const loop = () => {
      if (!userActiveRef.current) {
        const pos = simPosRef.current;
        const tgt = simTargetRef.current;
        pos.x += (tgt.x - pos.x) * speed;
        pos.y += (tgt.y - pos.y) * speed;
        tiltAt(pos.y, pos.x);
        if (Math.hypot(pos.x - tgt.x, pos.y - tgt.y) < 0.1) {
          simTargetRef.current = {
            x: Math.random() * gridSize,
            y: Math.random() * gridSize,
          };
        }
      }
      simRAFRef.current = requestAnimationFrame(loop);
    };
    simRAFRef.current = requestAnimationFrame(loop);
    return () => {
      if (simRAFRef.current != null) cancelAnimationFrame(simRAFRef.current);
    };
  }, [autoAnimate, reduced, gridSize, tiltAt]);

  // Global pointer-follow with per-cube lag: a window listener updates a single
  // target angle from the cursor, and a continuous rAF loop eases EACH cube
  // toward it at its own speed — so they trail the mouse individually instead of
  // all snapping together. When the pointer goes idle the target relaxes to a
  // gentle resting tilt (NOT flat) so the cubes always read as 3D, not squares.
  useEffect(() => {
    if (!followPointer || reduced) return;
    const el = sceneRef.current;
    if (!el) return;

    // Resting 3/4 tilt so the field looks like cubes immediately, before any
    // pointer interaction. Returned to whenever the cursor goes idle.
    const restX = -maxAngle * 0.45;
    const restY = maxAngle * 0.45;
    followTargetRef.current = { rotX: restX, rotY: restY };

    const onMove = (e: PointerEvent) => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const nx = Math.max(-1, Math.min(1, (e.clientX - cx) / followRange));
      const ny = Math.max(-1, Math.min(1, (e.clientY - cy) / followRange));
      followTargetRef.current = { rotX: -ny * maxAngle, rotY: nx * maxAngle };
      idleTimerRef.current = setTimeout(() => {
        followTargetRef.current = { rotX: restX, rotY: restY };
      }, 2600);
    };
    window.addEventListener('pointermove', onMove);

    // Per-cube state + a distinct follow speed (slower = laggier). The speed is
    // a stable function of the cube's position plus a hash, so neighbours differ.
    // Cubes START at the resting tilt so the 3D form is there instantly.
    const cubes = Array.from(el.querySelectorAll<HTMLElement>('.cube'));
    const state = cubes.map(cube => {
      const r = +cube.dataset.row!;
      const c = +cube.dataset.col!;
      const h = ((r * 73856093) ^ (c * 19349663)) >>> 0;
      const rnd = (h % 1000) / 1000; // 0..1, stable per cell
      const base = 0.05 + followStagger; // followStagger nudges overall responsiveness
      return { cube, rotX: restX, rotY: restY, speed: base * (0.35 + 0.65 * rnd) };
    });
    const setters = cubes.map(cube => ({
      rx: gsap.quickSetter(cube, 'rotateX', 'deg'),
      ry: gsap.quickSetter(cube, 'rotateY', 'deg'),
    }));

    const loop = () => {
      const t = followTargetRef.current;
      for (let i = 0; i < state.length; i++) {
        const s = state[i];
        s.rotX += (t.rotX - s.rotX) * s.speed;
        s.rotY += (t.rotY - s.rotY) * s.speed;
        setters[i].rx(s.rotX);
        setters[i].ry(s.rotY);
      }
      followRAFRef.current = requestAnimationFrame(loop);
    };
    followRAFRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('pointermove', onMove);
      if (followRAFRef.current != null) cancelAnimationFrame(followRAFRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [followPointer, reduced, followRange, maxAngle, followStagger, gridSize]);

  // Twinkle: each cube independently fades out and back in at its own random
  // cadence, so cubes vanish/reappear individually instead of the whole field
  // blinking at once. Runs on opacity only, so it never fights the tilt loop.
  useEffect(() => {
    if (!twinkle || reduced) return;
    const el = sceneRef.current;
    if (!el) return;
    const cubes = Array.from(el.querySelectorAll<HTMLElement>('.cube'));
    // Quick staggered fade-in on (re)appear — the full field is there fast, just
    // not as one hard block.
    cubes.forEach(cube => {
      gsap.fromTo(
        cube,
        { opacity: 0 },
        { opacity: 1, duration: 0.18, delay: Math.random() * 0.28, ease: 'power2.out' }
      );
    });
    // Continuous, independent per-cube twinkle. Quick fade out/in (so the field
    // mostly stays whole) with a long, randomized gap between blinks.
    const tweens = cubes.map(cube =>
      gsap.to(cube, {
        opacity: 0,
        duration: 0.2 + Math.random() * 0.18,
        repeat: -1,
        yoyo: true,
        repeatDelay: 4 + Math.random() * 6,
        delay: 1 + Math.random() * 7,
        ease: 'power2.inOut',
      })
    );
    return () => {
      tweens.forEach(t => t.kill());
      gsap.killTweensOf(cubes);
    };
  }, [twinkle, reduced, gridSize]);

  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return;

    // When following the pointer globally, skip the hover-only handlers so the
    // field doesn't reset as the cursor leaves the tiny grid.
    if (!followPointer) {
      el.addEventListener('pointermove', onPointerMove);
      el.addEventListener('pointerleave', resetAll);
    }
    el.addEventListener('click', onClick);

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerleave', resetAll);
      el.removeEventListener('click', onClick);

      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);

      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [followPointer, onPointerMove, resetAll, onClick, onTouchMove, onTouchStart, onTouchEnd]);

  const cells = Array.from({ length: gridSize });
  const sceneStyle: React.CSSProperties = {
    gridTemplateColumns: cubeSize ? `repeat(${gridSize}, ${cubeSize}px)` : `repeat(${gridSize}, 1fr)`,
    gridTemplateRows: cubeSize ? `repeat(${gridSize}, ${cubeSize}px)` : `repeat(${gridSize}, 1fr)`,
    columnGap: colGap,
    rowGap: rowGap,
    perspective: '99999999px',
    gridAutoRows: '1fr',
  };
  const wrapperStyle: React.CSSProperties = {
    ['--cube-face-border' as string]: borderStyle,
    ['--cube-face-bg' as string]: faceColor,
    ['--cube-face-shadow' as string]: shadow === true ? '0 0 6px rgba(0,0,0,.5)' : shadow || 'none',
    ...(cubeSize
      ? {
          width: `${gridSize * cubeSize}px`,
          height: `${gridSize * cubeSize}px`,
        }
      : {}),
  };

  const faceStyle: React.CSSProperties = {
    background: 'var(--cube-face-bg)',
    border: 'var(--cube-face-border)',
    boxShadow: 'var(--cube-face-shadow)',
  };

  return (
    <div className={`relative ${className ?? 'w-1/2 max-md:w-11/12 aspect-square'}`} style={wrapperStyle}>
      <div ref={sceneRef} className="grid w-full h-full" style={sceneStyle}>
        {cells.map((_, r) =>
          cells.map((__, c) => (
            <div
              key={`${r}-${c}`}
              className="cube relative w-full h-full aspect-square [transform-style:preserve-3d]"
              data-row={r}
              data-col={c}
            >
              <span className="absolute pointer-events-none -inset-9" />
              <div className="cube-face absolute inset-0 flex items-center justify-center" style={{ ...faceStyle, transform: 'translateY(-50%) rotateX(90deg)' }} />
              <div className="cube-face absolute inset-0 flex items-center justify-center" style={{ ...faceStyle, transform: 'translateY(50%) rotateX(-90deg)' }} />
              <div className="cube-face absolute inset-0 flex items-center justify-center" style={{ ...faceStyle, transform: 'translateX(-50%) rotateY(-90deg)' }} />
              <div className="cube-face absolute inset-0 flex items-center justify-center" style={{ ...faceStyle, transform: 'translateX(50%) rotateY(90deg)' }} />
              <div className="cube-face absolute inset-0 flex items-center justify-center" style={{ ...faceStyle, transform: 'rotateY(-90deg) translateX(50%) rotateY(90deg)' }} />
              <div className="cube-face absolute inset-0 flex items-center justify-center" style={{ ...faceStyle, transform: 'rotateY(90deg) translateX(-50%) rotateY(-90deg)' }} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Cubes;
